import clsx from 'clsx';

import { Loader } from 'common';
import { Layout } from 'components/layout';
import { CardsMini } from 'components/slider/cards/mini';
import { HeaderCollection } from './header';

import { CustomMetadata } from 'components/metadata';
import { allMetadata } from 'constants/metadata';
import { allSlots } from 'constants/routes-breadcrumbs';

import { FAQComponent } from 'components/casino-faq';
import { LoadMoreButton } from 'components/load-more-button';
import { ISlot } from 'constants/types';
import { useSlotsPagination } from 'hooks/useSlotsPagination';
import { SlotsDescription } from './description';
import styles from './styles.module.css';
import { Search } from '../home/search';

export const AllSlotsPage = ({ slots }: { slots?: ISlot[] }) => {
  const {
    page,
    isLast,
    setPage,
    isFetching,
    data: slotsPagination = [],
  } = useSlotsPagination({ collection: null });

  const data = slotsPagination?.length ? slotsPagination : (slots ?? []);

  if (!data?.length) {
    return <Loader size="big" />;
  }

  return (
    <div className={styles.container}>
      <CustomMetadata metadata={allMetadata.allSlots} />
      <Layout hasFooter isSearchInputMobileVisible bgVariant="basic">
        <main className={clsx(styles.main, styles.main_collection)}>
          <HeaderCollection items={allSlots} />

          <Search
            variant={'slots'}
            placeholder="Check out any slot game and its RTP"
            rounded
          />

          <CardsMini
            data={data}
            title={'Real RTP of Online Slots'}
            description={
              'To find out the possible RTP of any slot game and how to check it, simply enter the name of the game in the search bar above. We provide detailed information to help you make the best choices.'
            }
          />

          {data?.length > 0 && !isLast && (
            <>
              <LoadMoreButton
                isAuto={false}
                isVisible={!isLast}
                isFetching={isFetching}
                onClick={() => setPage(page + 1)}
                className={styles.load_more_button}
                description="Load more"
                variant="transparent"
              />
              <div className={styles.bottom} />
            </>
          )}

          <SlotsDescription />

          <FAQComponent
            customFaq={[
              {
                title: 'What is RTP in online casino games?',
                description:
                  'RTP stands for Return to Player, and it represents the percentage of all wagered money that a game is expected to pay back to players over time. For example, an RTP of 95% means that, on average, the game returns 95% of the bets to players in the long run.',
              },
              {
                title: 'Is a higher RTP better for players?',
                description:
                  "Yes, a higher RTP generally means better odds for players. Games with a higher RTP return more of the player's bet over time compared to those with lower RTP. However, RTP is based on long-term averages and does not guarantee results in the short term.",
              },
              {
                title: 'Can I always trust the RTP displayed by a casino game?',
                description:
                  'Yes, as long as the casino is licensed and reputable, the RTP information is reliable. The RTP percentages are calculated and audited by independent agencies, but it’s always a good idea to double-check on the provider’s website or in the game’s information section.',
              },
              {
                title: 'Can RTP vary between different casinos?',
                description:
                  'No, the RTP of a game remains fixed regardless of the casino you play at. However, the way the game is presented or the bonus features might differ, but the underlying RTP percentage is consistent across all licensed casinos offering the same version of the game.',
              },
              {
                title: 'Does the RTP apply to individual gaming sessions?',
                description:
                  "No, RTP is an average percentage calculated over a large number of spins or hands. It doesn't guarantee outcomes in a single session or for an individual player. For example, you might experience a win or loss that deviates significantly from the expected RTP in the short term.",
              },
            ]}
          />
        </main>
      </Layout>
    </div>
  );
};
